import { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Bot, HardHat, Truck, Banknote, Calculator as CalcIcon, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCart } from '../context/CartContext';

const GREETING = 'Halo! Saya Asisten Solusi Bangun Beton. Saya bisa bantu cek harga & produk, hitung kebutuhan beton, tambahkan ke keranjang, dan lacak status pesanan. Ada yang bisa saya bantu?';
const CUSTOMER_SERVICE_URL = 'https://wa.me/6281110008888?text=Halo%20Customer%20Service%20Solusi%20Bangun%20Beton%2C%20saya%20membutuhkan%20bantuan%20lebih%20lanjut.';

function normalizeAssistantMarkdown(text) {
  return String(text || '')
    .replaceAll('Â±', '±')
    .replaceAll('mł', 'm³');
}

function AssistantMarkdown({ children }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children: tableChildren }) => <div className="cwf-table-wrap"><table>{tableChildren}</table></div>,
        a: ({ children: linkChildren, href }) => <a href={href} target="_blank" rel="noreferrer">{linkChildren}</a>
      }}
    >
      {normalizeAssistantMarkdown(children)}
    </ReactMarkdown>
  );
}

export default function Chatbot() {
  const { addToCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('sbb_chat_session_id') || null);
  const [lastFailedMessage, setLastFailedMessage] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const loadHistory = async (sid) => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/chat/${sid}/history`);
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map(m => ({ role: m.role, text: m.content })));
      } else {
        setMessages([{ role: 'assistant', text: GREETING }]);
      }
    } catch {
      setMessages([{ role: 'assistant', text: GREETING }]);
    }
  };

  const handleToggleChat = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);

    if (willOpen && messages.length === 0) {
      if (sessionId) loadHistory(sessionId);
      else setMessages([{ role: 'assistant', text: GREETING }]);
    }
  };

  const sendToBackend = async (text) => {
    setLoading(true);
    setLastFailedMessage(null);
    try {
      const token = localStorage.getItem('sbb_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: sessionId, message: text })
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();

      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('sbb_chat_session_id', data.session_id);
      }

      let cartAdded = 0;
      if (data.cart_action?.items?.length) {
        data.cart_action.items.forEach(item => {
          addToCart(
            { id: item.product_id, sku: item.sku, name: item.name, category: item.category, image_url: item.image_url, price_per_m3: item.unit_price },
            item.volume_m3,
            item.truck_type
          );
          cartAdded += 1;
        });
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.message,
        cartAdded,
        needsHuman: Boolean(data.needs_human)
      }]);
    } catch {
      setLastFailedMessage(text);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Maaf, saya belum dapat memproses pertanyaan ini. Silakan coba lagi atau hubungi Customer Service kami.',
        error: true,
        needsHuman: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = (msgOverride) => {
    const userMsg = msgOverride || input;
    if (!userMsg.trim() || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    if (!msgOverride) setInput('');
    sendToBackend(userMsg);
  };

  const handleRetry = () => {
    if (!lastFailedMessage) return;
    setMessages(prev => prev.filter(m => !m.error));
    sendToBackend(lastFailedMessage);
  };

  const handleEndChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${import.meta.env.VITE_API_URL}/api/chat/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });
      } catch {
        // ignore network error on end — still reset locally
      }
    }
    localStorage.removeItem('sbb_chat_session_id');
    setSessionId(null);
    setMessages([{ role: 'assistant', text: GREETING }]);
  };

  return (
    <div className="chat-float">
      <div className={`cwf ${isOpen ? 'open' : ''}`}>
        <div className="cwf-hd">
          <div className="cwf-av"><Bot size={20} color="white" /></div>
          <div>
            <h4>Asisten SBB</h4>
            <p>Sales + Engineer · Online</p>
          </div>
          <button className="cwf-end" onClick={handleEndChat} title="Akhiri percakapan"><RotateCcw size={14} /></button>
          <button className="cwf-close" onClick={() => setIsOpen(false)}><X size={14} /></button>
        </div>
        <div className="cwf-body" ref={bodyRef} aria-live="polite">
          {messages.map((m, i) => (
            <div key={i} className={`cwf-msg ${m.role === 'user' ? 'usr' : 'bot'}`}>
              {m.role !== 'user' && <div className="cwf-av2"><Bot size={14} color="white" /></div>}
              <div className={`cwf-bbl ${m.error ? 'cwf-bbl-error' : ''}`}>
                {m.role === 'user' ? m.text : <AssistantMarkdown>{m.text}</AssistantMarkdown>}
                {m.cartAdded > 0 && <div className="cwf-cart-chip">✓ Ditambahkan ke keranjang ({m.cartAdded} item)</div>}
                {m.error && <button className="cwf-retry" onClick={handleRetry}>Coba lagi</button>}
                {m.needsHuman && (
                  <a className="cwf-customer-service" href={CUSTOMER_SERVICE_URL} target="_blank" rel="noreferrer">
                    Hubungi Customer Service
                  </a>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="cwf-msg bot">
              <div className="cwf-av2"><Bot size={14} color="white" /></div>
              <div className="cwf-bbl cwf-typing"><span></span><span></span><span></span></div>
            </div>
          )}
        </div>
        <div className="cwf-chips">
          <button className="cwf-chip" onClick={() => { setIsOpen(true); handleSend('Hitung kebutuhan beton saya'); }}>
            <CalcIcon size={12} style={{ marginRight: '4px' }} /> Hitung Beton
          </button>
          <button className="cwf-chip" onClick={() => { setIsOpen(true); handleSend('Berapa harga beton K250?'); }}>
            <Banknote size={12} style={{ marginRight: '4px' }} /> Cek Harga
          </button>
          <button className="cwf-chip" onClick={() => { setIsOpen(true); handleSend('Cek status order saya'); }}>
            <Truck size={12} style={{ marginRight: '4px' }} /> Lacak Order
          </button>
          <button className="cwf-chip" onClick={() => { setIsOpen(true); handleSend('Konsultasi engineer'); }}>
            <HardHat size={12} style={{ marginRight: '4px' }} /> Konsultasi
          </button>
        </div>
        <div className="cwf-inp-row">
          <input
            type="text"
            className="cwf-inp"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ketik pesan..."
            disabled={loading}
          />
          <button className="cwf-send" onClick={() => handleSend()} disabled={loading}><Send size={16} /></button>
        </div>
      </div>
      <button
        type="button"
        className="chat-fab"
        onClick={handleToggleChat}
        aria-label={isOpen ? 'Tutup konsultasi AI' : 'Buka konsultasi AI'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={20} /> : <MessageSquare size={21} />}
        <span>{isOpen ? 'Tutup Chat' : 'Hubungi Kami'}</span>
        {!isOpen && <div className="fab-dot"></div>}
      </button>
    </div>
  );
}
